import { LOG_CATEGORIES, logService } from '@/services/logService'

type DeviceOrientationWithWebKitHeading = DeviceOrientationEvent & {
  webkitCompassHeading?: number
}

type DeviceOrientationEventConstructorWithPermission = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>
}

type HeadingListener = (heading: number | null) => void

function normalizeHeading(heading: number | null | undefined): number | null {
  if (heading == null || !Number.isFinite(heading)) return null
  return ((heading % 360) + 360) % 360
}

export function headingFromDeviceOrientation(
  event: DeviceOrientationEvent,
  screenAngle = 0,
  isAbsoluteEvent = false,
): number | null {
  const webkitHeading = (event as DeviceOrientationWithWebKitHeading).webkitCompassHeading
  if (Number.isFinite(webkitHeading) && (webkitHeading as number) >= 0) {
    return normalizeHeading(webkitHeading as number)
  }

  if ((!event.absolute && !isAbsoluteEvent) || !Number.isFinite(event.alpha)) return null
  return normalizeHeading(360 - (event.alpha as number) + screenAngle)
}

function getScreenAngle(): number {
  const legacyOrientation = (window as Window & { orientation?: number }).orientation
  return window.screen.orientation?.angle ?? legacyOrientation ?? 0
}

class DeviceHeadingService {
  private readonly listeners = new Set<HeadingListener>()
  private heading: number | null = null
  private permissionGranted = false
  private permissionRequest: Promise<boolean> | null = null

  subscribe(listener: HeadingListener): () => void {
    this.listeners.add(listener)
    if (this.listeners.size === 1) this.startListening()
    listener(this.heading)

    return () => {
      this.listeners.delete(listener)
      if (this.listeners.size === 0) this.stopListening()
    }
  }

  async requestPermission(): Promise<boolean> {
    if (this.permissionGranted) return true

    const eventConstructor = globalThis.DeviceOrientationEvent as
      | DeviceOrientationEventConstructorWithPermission
      | undefined
    if (!eventConstructor?.requestPermission) {
      this.permissionGranted = true
      return true
    }

    if (this.permissionRequest) return this.permissionRequest

    try {
      const request = eventConstructor
        .requestPermission()
        .then((result) => {
          this.permissionGranted = result === 'granted'
          return this.permissionGranted
        })
        .catch((error: unknown) => {
          logService.warn(LOG_CATEGORIES.MAP, 'Failed to request device orientation permission', {
            error,
          })
          return false
        })
      this.permissionRequest = request
      void request.then(() => {
        if (this.permissionRequest === request) this.permissionRequest = null
      })
      return request
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Failed to request device orientation permission', {
        error,
      })
      return false
    }
  }

  private readonly handleAbsoluteOrientation = (event: DeviceOrientationEvent) => {
    this.publishHeading(headingFromDeviceOrientation(event, getScreenAngle(), true))
  }

  private readonly handleOrientation = (event: DeviceOrientationEvent) => {
    this.publishHeading(headingFromDeviceOrientation(event, getScreenAngle()))
  }

  private publishHeading(heading: number | null): void {
    if (heading == null) return
    this.heading = heading
    this.listeners.forEach((listener) => listener(heading))
  }

  private startListening(): void {
    window.addEventListener(
      'deviceorientationabsolute',
      this.handleAbsoluteOrientation as EventListener,
    )
    window.addEventListener('deviceorientation', this.handleOrientation)
  }

  private stopListening(): void {
    window.removeEventListener(
      'deviceorientationabsolute',
      this.handleAbsoluteOrientation as EventListener,
    )
    window.removeEventListener('deviceorientation', this.handleOrientation)
    this.heading = null
  }
}

export const deviceHeadingService = new DeviceHeadingService()
